import TurndownService from "turndown";

import documentIcon from "../assets/icons/document.svg";
import imageIcon from "../assets/icons/image.svg";
import linkIcon from "../assets/icons/link.svg";
import videoIcon from "../assets/icons/video.svg";
import { MIME_TYPES } from "../constants";
import { ICON_TYPES } from "../types/enum/enum";

export const calcFileSize = (size: number): string => {
    if (size < 0) {
        throw new Error("Size must be a non-negative number");
    }

    const units = ["B", "KB", "MB", "GB", "TB"];
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
};

export const checkObjValueExists = (obj: object) => {
    return Object.values(obj).every((item) => item !== null && item !== undefined && item !== "");
};

export const mapMimeTypeKey = (mimeType: string): string | undefined => {
    const entry = Object.entries(MIME_TYPES).find(([key, value]) => value === mimeType);
    return entry ? entry[0] : undefined;
};
export const getMimeHelper = {
    // 將 MIME 類型映射為文件擴展名
    mapMimeTypeKey: (mimeType: string): string | undefined => {
        const entry = Object.entries(MIME_TYPES).find(([key, value]) => value === mimeType);
        return entry ? entry[0] : undefined; // 返回 MIME 類型的鍵（文件擴展名）
    },

    // 將 MIME 類型陣列轉換為文件擴展名陣列
    getFileExtensions: (mimeTypes: string[]): string[] => {
        return mimeTypes.map((mimeType) => getMimeHelper.mapMimeTypeKey(mimeType) || mimeType);
    },

    // 將文件擴展名陣列轉換為 MIME 類型陣列
    getMimeTypes: (extensions: string[]): string[] => {
        return extensions.map((ext) => MIME_TYPES[ext] || ext);
    },
};
export const isProduction = import.meta.env.VITE_ENV === "production";
export const isStaging = import.meta.env.VITE_ENV === "staging";
export const isDev = import.meta.env.VITE_ENV === "development";

export const tryCatchAsync = async <T>(dataFetching: () => Promise<T>) => {
    try {
        const data = await dataFetching();
        return [data, null];
    } catch (err) {
        return [null, err];
    }
};

export const getIcon = (iconType: ICON_TYPES) => {
    const iconSrc = {
        video: videoIcon,
        document: documentIcon,
        image: imageIcon,
        link: linkIcon,
    };
    return iconSrc[iconType];
};

//only for voiceKeyword now
export const getLanguage = (lang: string) => {
    const normalizedLang = lang.toLowerCase();
    const mappingObj: Record<string, string> = {
        "en-us": "en",
        "ja-jp": "ja",
        "zh-tw": "zh",
        "vi-vn": "vi",
        "th-th": "th",
        "id-id": "id",
    };
    return mappingObj[normalizedLang] ?? "zh-TW";
};

export const getLocalValueByKey = (key: string): string | null => {
    const value = localStorage.getItem(key);
    return value !== "null" && value !== "undefined" && value !== "" ? value : null;
};
export const formatTime = (time: number) => {
    // time 須為"秒", e.g. 5 = 5 秒
    if (time < 0) {
        throw new Error("Time must be a non-negative number");
    }

    const seconds = Math.floor(time % 60);
    const minutes = Math.floor((time % 3600) / 60);
    const hours = Math.floor(time / 3600);

    const format = (value: number) => (value >= 10 ? value.toString() : `0${value}`);

    return {
        getSeconds: () => format(seconds),
        getMinutes: () => format(minutes),
        getHours: () => format(hours),
    };
};

export const scrollToTop = () => {
    window.scrollTo(0, 0);
};

// 空行標記符號，用於在 markdown 轉換過程中保留空行
const EMPTY_LINE_PLACEHOLDER = "___EMPTY_LINE___";

export const htmlToMarkDown = (htmlContent: string) => {
    // 預處理：將空的段落標籤內容替換為 placeholder 文字
    // 這樣 turndown 會將其視為普通文字段落處理
    const processedHtml = htmlContent
        .replace(/<p><br\s*\/?><\/p>/gi, `<p>${EMPTY_LINE_PLACEHOLDER}</p>`)
        .replace(/<p>\s*<\/p>/gi, `<p>${EMPTY_LINE_PLACEHOLDER}</p>`)
        .replace(/<p>&nbsp;<\/p>/gi, `<p>${EMPTY_LINE_PLACEHOLDER}</p>`)
        .replace(/<p>\u00A0<\/p>/gi, `<p>${EMPTY_LINE_PLACEHOLDER}</p>`);

    const turndown = new TurndownService({ headingStyle: "atx", bulletListMarker: "-", codeBlockStyle: "fenced" });
    turndown.keep([
        "s",
        "span",
        "strike",
        "sub",
        "sup",
        "code",
        "em",
        "strong",
        "img",
        "br",
        "hr",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "ol",
        "ul",
        "li",
        "img",
        "pre",
    ]);

    //對於粗體和強調標籤，保留內容
    turndown.addRule("strongAndEmphasis", {
        filter: ["strong", "em"],
        replacement: (content: string, node: { nodeName: string }) => {
            const tag = node.nodeName.toLowerCase();
            return `<${tag}>${content}</${tag}>`;
        },
    });
    //保留所有樣式
    turndown.addRule("preserveInlineStyles", {
        filter: (node: { nodeType: number; hasAttribute: (arg0: string) => string }) =>
            node.nodeType === 1 && (node.hasAttribute("style") || node.hasAttribute("class")),
        replacement: (
            content: string,
            node: { tagName: string; getAttribute: (arg0: string) => string; hasAttribute: (arg0: string) => string }
        ) => {
            const tagName = node.tagName.toLowerCase();

            const styleString = node.hasAttribute("style") ? `style="${node.getAttribute("style")}"` : "";
            const classString = node.hasAttribute("class") ? `class="${node.getAttribute("class")}"` : "";

            return `<${tagName} ${styleString} ${classString}>${content}</${tagName}>`;
        },
    });
    //保留圖片寬
    // turndown.addRule("preserveImageDimensions", {
    //     filter: (node: { nodeType: number; tagName: string; hasAttribute: (arg0: string) => boolean }) =>
    //         node.nodeType === 1 && node.tagName.toLowerCase() === "img" ,
    //     replacement: (node: { getAttribute: (arg0: string) => string }) => {
    //         if (node && node.getAttribute) {
    //             const width = node.getAttribute("width") ? `width=${node.getAttribute("width")}` : "";
    //             const height = node.getAttribute("height") ? `height=${node.getAttribute("height")}` : "";
    //             const alt = node.getAttribute("alt") ? `alt=${node.getAttribute("alt")}` : "";
    //             const attributes = [width, height, alt].filter((attr) => attr).join(" ");
    //             return `<img src="${node.getAttribute("src")}" ${attributes}/>`;
    //         }
    //     },
    // });
    //變成undefined

    //保留圖片寬，可以保有大小的寫法
    turndown.addRule("preserveImageAttributes", {
        filter: ["img"],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        replacement: (content: any, node: any) => {
            // 使用類似您的寫法檢查和獲取屬性
            // const srcString = node.src ? `src="${node.src}"` : "";
            const altString = node.alt ? `alt="${node.alt}"` : "";
            const widthString = node.width ? `width="${node.width}"` : "";
            const heightString = node.height ? `height="${node.height}"` : "";

            const attributes = `${widthString} ${heightString} ${altString}`;

            return `<img src="${node.src}" ${attributes}>`;
        },
    });

    // 自訂處理器 - 保留 <ol> 為 HTML
    turndown.addRule("olistItemAsHtml", {
        filter: "ol",
        replacement: (
            content: string,
            node: { getAttribute: (arg0: string) => string; hasAttribute: (arg0: string) => boolean }
        ) => {
            const styleString = node.hasAttribute("style") ? `style="${node.getAttribute("style")}"` : "";
            const classString = node.hasAttribute("class") ? `class="${node.getAttribute("class")}"` : "";

            return `<ol ${styleString} ${classString}>${content}</ol>`;
        },
    });
    turndown.addRule("ulistItemAsHtml", {
        filter: "ul",
        replacement: (
            content: string,
            node: { getAttribute: (arg0: string) => string; hasAttribute: (arg0: string) => boolean }
        ) => {
            const classString = node.hasAttribute("class") ? `class="${node.getAttribute("class")}"` : "";
            const styleString = node.hasAttribute("style") ? `style="${node.getAttribute("style")}"` : "";
            return `<ul ${styleString} ${classString}>${content}</ul>`;
        },
    });
    // 自訂處理器 - 保留 <li> 為 HTML
    turndown.addRule("listItemAsHtml", {
        filter: "li",
        replacement: (
            content: string,
            node: { getAttribute: (arg0: string) => string; hasAttribute: (arg0: string) => boolean }
        ) => {
            const styleString = node.hasAttribute("style") ? `style="${node.getAttribute("style")}"` : "";
            const classString = node.hasAttribute("class") ? `class="${node.getAttribute("class")}"` : "";
            return `<li ${styleString} ${classString}>${content}</li>`;
        },
    });

    turndown.remove("script");

    let markdown = turndown.turndown(processedHtml);

    /** 修正因 TinyMCE 自動鏈接導致格式錯誤的 Markdown 鏈接
     * 範例：\[text\]([url](url)) 或 [text]([url](url)) -> [text](url)
     * 處理當用戶輸入 Markdown 鏈接語法且 TinyMCE 又自動鏈接網址的情況
     */
    const malformedLinkRegex = /\\?\[([^\]]+)\\?\]\(\[([^\]]+)\]\(([^)]+)\)\)/g;
    markdown = markdown.replace(malformedLinkRegex, (_, text, _linkText, url) => {
        return `[${text}](${url})`;
    });

    /** 修正原本應該作為有效鏈接的括號問題： \[text\](url) -> [text](url)
     * 只在 url 看起來是有效網址時才轉換
     */
    const escapedBracketsRegex = /\\?\[([^\]]+)\\?\]\(([^[\]()]+)\)/g;
    markdown = markdown.replace(escapedBracketsRegex, (_, text, url) => {
        if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("/")) {
            return `[${text}](${url})`;
        }
        return `[${text}](${url})`;
    });

    return markdown;
};

const excludeBracketedTextRegex = /\s*\(.*?\)\s*/g;
export const translateLang = ({ locale, currentLang }: { locale: string; currentLang: string }) => {
    // 部份語言特殊處理
    if (currentLang === "en-US") {
        switch (locale) {
            case "zh-TW":
                return "Traditional Chinese";
            case "zh-CN":
                return "Simplified Chinese";
            case "ja-JP":
                return "Japanese";
            case "ko-KR":
                return "Korean";
            case "vi-VN":
                return "Vietnamese";
            case "th-TH":
                return "Thai";
            case "en-US":
                return "English";
            default:
                break;
        }
    }

    const name = new Intl.DisplayNames([currentLang], { type: "language", style: "short" }).of(locale) || "";
    return name.replace(excludeBracketedTextRegex, "");
};

export const getLocalTime = (isoString: string | undefined) => {
    if (isoString !== undefined) {
        const date = new Date(isoString);
        return new Intl.DateTimeFormat(undefined, {
            timeStyle: "medium",
            hour12: false, //18:48
            dateStyle: "short", //2024/3/25
        }).format(date);
    }
};

/**
 * 計算 HTML 內容的純文字字數，移除所有 HTML 標記
 * @param htmlContent - HTML 格式的內容
 * @returns 純文字的字數
 */
export const getTextCountFromHtml = (htmlContent: string): number => {
    if (!htmlContent) return 0;

    // 創建臨時 DOM 元素來解析 HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    // 獲取純文字內容
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    // 返回純文字長度
    return textContent.length;
};
